import { type ApolloClient, type NormalizedCacheObject } from '@apollo/client';
import { addTeamMember } from '~/api/data/teams/mutations';
import { createAgencyUsers, updateAgencyUser } from '~/api/data/users/mutations';
import { type AgencyUserImportInput, Role, type CreateAgencyUsersMutation } from '~/api/graphql';
import {
  changeId,
  csvSchemaFromServer,
  joinList,
  splitList,
  type CommitProgress,
  type CommitFailure,
  type CommitResult,
  type CsvSchema,
  type DiffChange,
  type SelectedChanges,
  type ServerImportSchema
} from '~/components/csv-import';
import {
  ADMIN_OPTION,
  decodeUserProfileOption,
  DEFAULT_PROFILE_OPTION,
  profileOptionValue
} from './user-profile-options';

/** A named thing an agency defines, such as an access profile or a team. */
type Named = { id: string; name: string };

/** What the import needs to know about the agency's users and what it has defined. */
export type UserImportContext = {
  profiles: Named[];
  /** Undefined when the agency cannot use teams, which leaves the column out. */
  teams?: Named[];
  /** The profile assigned directly to each user, by user id. */
  profileOfUser: Map<string, string>;
  /** The ids of each user's teams, by user id. */
  teamsOfUser: Map<string, string[]>;
  /** The importing admin's email; nobody can change their own access. */
  selfEmail?: string;
};

/** Must match accessAdmin in agency_user_import.go, which lists the choices. */
const ADMIN_ACCESS = 'Admin';

/** The Access value for a user with no profile of their own, as agencyUserImportSchemaFor names it. */
const defaultAccess = (profiles: Named[]) => (profiles.length > 0 ? 'Default' : 'User');

/**
 * The server's user import columns, with the page's behaviour on top: Access is a
 * permissions change worth the reviewer's eye, and nobody can change their own.
 */
export const userImportSchema = (
  server: ServerImportSchema,
  { selfEmail }: Pick<UserImportContext, 'selfEmail'>
): CsvSchema =>
  csvSchemaFromServer(server, {
    behaviours: {
      access: {
        sensitive: true,
        lockedFor: (current) =>
          selfEmail && current.email.toLowerCase() === selfEmail.toLowerCase()
            ? "You can't change your own access"
            : undefined
      }
    }
  });

export const userToRecord = (
  user: { id: string; name: string; email: string; roles: Role[] },
  { profiles, teams, profileOfUser, teamsOfUser }: UserImportContext
): Record<string, string> => {
  const profileName = profiles.find((profile) => profile.id === profileOfUser.get(user.id))?.name;
  const teamNames = (teamsOfUser.get(user.id) ?? []).map(
    (id) => teams?.find((team) => team.id === id)?.name ?? id
  );
  return {
    name: user.name,
    email: user.email,
    access: user.roles.includes(Role.Admin)
      ? ADMIN_ACCESS
      : (profileName ?? defaultAccess(profiles)),
    teams: joinList(teamNames)
  };
};

/** The roles and direct profile an Access value stands for. */
const accessChoice = (access: string, profiles: Named[]) => {
  if (access === ADMIN_ACCESS) {
    return decodeUserProfileOption(ADMIN_OPTION);
  }
  const profile = profiles.find((candidate) => candidate.name === access);
  return decodeUserProfileOption(profile ? profileOptionValue(profile.id) : DEFAULT_PROFILE_OPTION);
};

const teamIds = (cell: string, teams: Named[] = []) =>
  splitList(cell).flatMap((name) => teams.find((team) => team.name === name)?.id ?? []);

const SAVE_FAILED = 'Something went wrong saving this user.';

const messageOf = (error: unknown) => (error instanceof Error ? error.message : SAVE_FAILED);

/** The most users the server creates in one call. */
export const CREATE_BATCH_SIZE = 100;

export const normalizedEmail = (email: string) => email.trim().toLowerCase();

/**
 * Writes an import. New users are created in batches, each checked and written by the
 * server in one call, so a failed row is reported against itself and the rest of its
 * batch still lands. Existing users have any new teams added, then their access updated,
 * one user at a time. Access goes last so that if adding a team fails, the user's access
 * is left as it was.
 */
export const commitUserImport = async (
  changes: SelectedChanges,
  {
    client,
    context,
    userIdByEmail,
    reportProgress,
    idempotencyKeyFor
  }: {
    client: ApolloClient<NormalizedCacheObject>;
    context: UserImportContext;
    userIdByEmail: Map<string, string>;
    reportProgress?: CommitProgress;
    /** The key to send with a batch, so a retry of it creates no one twice. */
    idempotencyKeyFor?: (inputs: AgencyUserImportInput[]) => string;
  }
): Promise<CommitResult> => {
  const failures: CommitFailure[] = [];
  let written = 0;
  const addToTeams = async (userId: string, ids: string[]) => {
    for (const teamId of ids) {
      const { error } = await addTeamMember({ teamId, userId }, client);
      if (error) throw error;
    }
  };
  const write = async (change: DiffChange, apply: () => Promise<void>) => {
    try {
      await apply();
    } catch (error) {
      failures.push({ id: changeId(change), message: messageOf(error) });
    }
  };

  for (let start = 0; start < changes.added.length; start += CREATE_BATCH_SIZE) {
    const batch = changes.added.slice(start, start + CREATE_BATCH_SIZE);
    let results: CreateAgencyUsersMutation['createAgencyUsers'];
    try {
      const inputs: AgencyUserImportInput[] = batch.map(({ values = {} }) => ({
        name: values.name,
        email: values.email,
        ...accessChoice(values.access, context.profiles),
        teamIds: teamIds(values.teams ?? '', context.teams)
      }));
      const { data, error } = await createAgencyUsers(
        { inputs, idempotencyKey: idempotencyKeyFor?.(inputs) },
        client
      );
      if (error) throw error;
      results = data?.createAgencyUsers ?? [];
    } catch (error) {
      // The server refused the whole batch, as when seats run out, and would refuse
      // every later one for the same reason; earlier batches are already written.
      const message = messageOf(error);
      for (const change of changes.added.slice(start)) {
        failures.push({ id: changeId(change), message });
      }
      break;
    }
    const resultByEmail = new Map(results.map((result) => [result.email, result]));
    for (const change of batch) {
      const result = resultByEmail.get(normalizedEmail(change.values?.email ?? ''));
      if (!result?.user) {
        failures.push({
          id: changeId(change),
          message: result?.error ?? SAVE_FAILED
        });
      }
    }
    written += batch.length;
    reportProgress?.(written);
  }

  for (const change of changes.updated) {
    const values = change.values ?? {};
    const id = userIdByEmail.get(normalizedEmail(values.email));
    const accessChange = change.fieldChanges?.find((fieldChange) => fieldChange.key === 'access');
    await write(change, async () => {
      if (!id) {
        throw new Error('This user could not be found.');
      }
      const current = new Set(context.teamsOfUser.get(id) ?? []);
      await addToTeams(
        id,
        teamIds(values.teams ?? '', context.teams).filter((teamId) => !current.has(teamId))
      );
      if (accessChange) {
        const { error } = await updateAgencyUser(
          {
            id,
            input: {
              ...accessChoice(values.access, context.profiles),
              // What the review showed, so a change someone made since is refused, not overwritten.
              expectedAccess: accessChoice(accessChange.from, context.profiles)
            }
          },
          client
        );
        if (error) throw error;
      }
    });
    written += 1;
    reportProgress?.(written);
  }

  return failures.length > 0 ? { status: 'partial', failures } : { status: 'applied' };
};
